import { SideBarItem } from "@common-components/sidebar/sidebar";
import ThemeAsset from "@common-components/theme/theme-asset";
import { appConfig } from "@common-config/app.config";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { useRouter } from "@common-reducers/hooks/use-router";
import { selectCurrentUser } from "@common-reducers/UserSelectors";
import { SidebarLinkButton } from "@common/components/sidebar/sidebar-link-button";
import { resolveActiveSidebarItem } from "@common/components/sidebar/sidebar-active-item";
import { isExtensionContext } from "@common/services/detect-context";
import { isClearFlow } from "@common/utils/clear-flow";
import { SetupProgress } from "@/pages/onboarding/setup-progress";
import { useSafeLocation } from "@common/services/utils";
import { CalendarMonth, Campaign, SmartToy, Task, SupportAgent, WhatsApp } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Stack } from "@mui/material";
import { useIsMobile } from "@common/hooks/use-is-mobile";
import classes from "./boards-panel.module.scss";
import { boardsActions } from "@common-reducers/BoardsReducer";
import { taskActions } from "@common-reducers/TasksReducer";
import { AIAssistantMenuItem } from "@common-components/ai-assistant";
import { AgentGlyph } from "@common-components/mascot/AgentGlyph";
import { DBBotThunks, botSelectByQuery } from "@common-reducers/DBServiceThunks";
import { useEffect } from "react";
import { ReferNavItem } from "@common-components/referral-share-card/refer-nav-item";

interface SideBarHeaderProps {
    drawerIsOpen: boolean;
    direction: string;
    isMinimized: boolean;
    onToggleMinimize: () => void;
    /** Called when a nav item is tapped (used to close the temporary drawer on mobile). */
    onNavigate?: () => void;
}

export default function SideBarHeader({ drawerIsOpen, direction, isMinimized, onToggleMinimize, onNavigate }: SideBarHeaderProps) {
    const router = useRouter();
    const dispatch = useAppDispatch();
    const location = useSafeLocation();

    // Which nav entry is highlighted for the current route. Audiences and Launch
    // Campaign share the /campaigns pathname (split by ?tab=), so the resolver
    // reads the query — see sidebar-active-item.ts.
    const active = resolveActiveSidebarItem(location.pathname, location.search);
    const user = useAppSelector(state => selectCurrentUser(state));
    const isExtension = isExtensionContext();
    const isMobile = useIsMobile();
    // The legacy agents (/bot-list-view) are shown only to accounts that already
    // have a legacy bot. Everyone else sees one "Agents" entry — the new agents
    // at /agents ("AI Agents"). Accounts with legacy bots keep both, the new one as "Agents 2.0".
    const hasLegacyBots = useAppSelector(state => botSelectByQuery(state.DBBotReducer, {}).length > 0);
    useEffect(() => {
        if (!isExtension && user?._id) dispatch(DBBotThunks.find({ $paginate: false }));
    }, [dispatch, isExtension, user?._id]);

    const onTasksClick = () => {
        dispatch(boardsActions.privateSetSelectedBoardId(''))
        dispatch(taskActions.setDeletedBoardName(''));
        dispatch(taskActions.setViewMode('tasks'))
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'auto' }} >
            <Box
                className={classes.panel_header}
                sx={{
                    minHeight: '64px',
                    px: '16px',
                    justifyContent: 'flex-start',
                    transition: 'padding 0.3s ease',
                }}
            >
                <Stack direction="row" alignItems="center" width="100%" justifyContent="space-between">
                    <Box sx={{
                        overflow: 'hidden',
                        transition: 'max-width 0.3s ease, opacity 0.2s ease',
                        maxWidth: isMinimized ? 0 : 200,
                        opacity: isMinimized ? 0 : 1,
                        flexShrink: 0,
                    }}>
                        <ThemeAsset
                            assetName='logo.svg'
                            onClick={() => router.navigate('/calendar')}
                            style={{ width: drawerIsOpen ? 'initial' : 32 }}
                            className={classes.logo}
                            alt={`${appConfig.DEFAULT_TITLE}-logo`}
                        />
                    </Box>
                    {!isExtension && (
                        // On MOBILE the drawer is a full-width temporary overlay, so
                        // "minimize width" is meaningless and left the user with no way
                        // to close it. There, this button CLOSES the drawer (X). On
                        // desktop it keeps the minimize/expand behaviour.
                        <IconButton
                            onClick={isMobile ? onNavigate : onToggleMinimize}
                            size="small"
                            aria-label={isMobile ? "close navigation menu" : undefined}
                            title={isMobile ? "Close menu" : (isMinimized ? "Expand sidebar" : "Minimize sidebar")}
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '10px',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                                flexShrink: 0,
                            }}
                        >
                            {isMobile ? <CloseIcon fontSize="small" /> : (isMinimized ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />)}
                        </IconButton>
                    )}
                </Stack>
            </Box>

            {/* Until the user is connected and their first message is out: the way back into setup. */}
            {!isExtension && !isClearFlow() && <SetupProgress minimized={isMinimized} />}

            <Box sx={{
                mx: 2, mb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                opacity: isMinimized ? 1 : 0,
                transition: 'opacity 0.3s ease',
            }} />

            {!isExtension && (
                <Stack
                    onClick={onNavigate}
                    sx={{
                        height: 'auto',
                        overflow: 'hidden',
                        alignItems: 'stretch',
                        gap: 0,
                    }}>
                    <SidebarLinkButton
                        to="/calendar"
                        icon={CalendarMonth}
                        titleKey="navigation.messagePlanner"
                        active={SideBarItem.Calendar}
                        currentActive={active}
                        analyticsAction="switch_to_calendar"
                        isMinimized={isMinimized}
                    />

                    <SidebarLinkButton
                        to="/campaigns"
                        icon={Campaign}
                        titleKey="navigation.launchCampaign"
                        active={SideBarItem.Campaigns}
                        currentActive={active}
                        analyticsAction="switch_to_campaigns"
                        isMinimized={isMinimized}
                    />

                    <SidebarLinkButton
                        to="/agents"
                        icon={AgentGlyph}
                        titleKey={hasLegacyBots ? "navigation.agents2" : "navigation.aiAgents"}
                        active={SideBarItem.MyBridge}
                        currentActive={active}
                        analyticsAction="switch_to_agents_2"
                        testId="agents-2-view-button"
                        badgeKey="navigation.new"
                        iconDot
                        isMinimized={isMinimized}
                    />

                    <AIAssistantMenuItem isMinimized={isMinimized} />

                    {/* Agent builders check their connections often, so this lives in the
                        main nav (it used to be buried in the account-settings panel). */}
                    <SidebarLinkButton
                        to="/connections"
                        icon={WhatsApp}
                        titleKey="sideBar.whatsAppConnections"
                        active={SideBarItem.WhatsAppConnections}
                        currentActive={active}
                        analyticsAction="switch_to_whatsapp_connections"
                        testId="whatsapp-connections-view-button"
                        isMinimized={isMinimized}
                    />

                    <SidebarLinkButton
                        to="/tasks"
                        icon={Task}
                        titleKey="navigation.tasks"
                        active={SideBarItem.Tasks}
                        currentActive={active}
                        analyticsAction="switch_to_tasks"
                        onClick={onTasksClick}
                        isMinimized={isMinimized}
                    />

                    <ReferNavItem currentActive={active} isMinimized={isMinimized} />

                    {hasLegacyBots && (
                        <SidebarLinkButton
                            to="/bot-list-view"
                            icon={SmartToy}
                            titleKey="navigation.agents"
                            active={SideBarItem.Agents}
                            currentActive={active}
                            analyticsAction="switch_to_agents"
                            isMinimized={isMinimized}
                        />
                    )}

                    {(user?.role === "support" || user?.role === "admin") && (
                        <SidebarLinkButton
                            to="/support"
                            icon={SupportAgent}
                            titleKey="navigation.support"
                            active={SideBarItem.Support}
                            currentActive={active}
                            analyticsAction="switch_to_support"
                            isMinimized={isMinimized}
                        />
                    )}
                </Stack>
            )}
        </Box>
    );
}
